import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/**
 * A one-page-scoped language switch for the App Patch Policy console page — staff-facing UI
 * chrome only (headings, labels, buttons, static help text). Deliberately NOT the same system as
 * web/src/features/i18n: that manages translations of the *mobile app's own* user-facing content
 * as data staff edit; this is just this page's own interface. Only static copy is translated —
 * promote/rollback outcome text and validation errors stay in whatever the server/zod returns,
 * since those are composed from live data (patch numbers, versions, error messages) that isn't
 * meaningfully "translatable" on its own.
 */
export type Locale = 'en' | 'zh'

const STORAGE_KEY = 'app-patch-policy-locale'

type Dict = Record<string, string>

const EN: Dict = {
  'page.title': 'App Patch Policy',
  'page.loadErrorTitle': 'Could not load the patch policy',
  'page.intro':
    'Shipping a Shorebird patch to real users: test it on a real device, then promote it — check "Also set as mandatory" on the way if this same patch should immediately become the force-restart floor too. The cards below are for checking the current floor or manually adjusting it later.',

  'common.tryAgain': 'Try again',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.clear': 'Clear',
  'common.translations': 'Translations',

  'actions.heading': 'I want to',
  'actions.promote': 'Ship a patch (verify + promote)',
  'actions.flag': 'Change mandatory / optional flag',
  'actions.roll': 'Emergency rollback',

  'summary.mandatoryOn': 'Mandatory floor set',
  'summary.mandatoryOff': 'No mandatory floor',

  'record.heading': 'Patch record',
  'record.empty': 'No recent activity yet.',
  'record.loadError': 'Could not load recent activity.',
  'record.viewAll': 'View full audit log →',

  'staging.title': 'Flip a device into staging mode',
  'staging.descBefore': 'On the test device itself — not here — open the app, go to ',
  'staging.descAfter':
    ', and tap the version label at the bottom 7 times within 3 seconds. A toast confirms staging mode is on. From then on, that device silently downloads and evaluates whatever patch is currently on the staging track, exactly like a real user\'s device does for stable — no extra command needed for each new patch. Tap it 7 more times to turn it back off.',

  'platform.notSyncedYet': 'not synced yet — reload this page once Shorebird is reachable',

  'rules.heading': 'Mandatory floors',
  'rules.help':
    'Each rule forces clients on exactly that release_version, on the platform(s) checked below, to at or above the given patch number. A release_version with no rule below has no floor at all — including a brand new release, which starts with none until you add one; patch numbers restart at 1 per release, so a rule from an older release never applies to a newer one.',
  'rules.loadError': 'Could not load mandatory floors. Reload to retry.',
  'rules.noneAny': 'No mandatory floors set for either platform.',
  'rules.pickFromStable': 'Pick from stable',
  'rules.pickerLoading': 'Loading…',
  'rules.pickerEmpty': 'Nothing on stable yet',
  'rules.pickerPrompt': 'Pick a patch live on stable',
  'rules.releaseVersionLabel': 'Release version',
  'rules.releaseVersionPlaceholder': 'e.g. 1.0.1+6',
  'rules.patchNumberLabel': 'Patch number',
  'rules.platformLabel': 'Platform',
  'rules.multiSelectHint': 'Multiple allowed',
  'rules.checkPlatformFirst': 'Check a platform to pick from its stable patches.',
  'rules.unavailableHint':
    "A platform stays unavailable to check until this exact release_version + patch number is also live on its own stable.",
  'rules.unchanged': 'already set to this — nothing to save.',
  'rules.unverified': '(not verified — Shorebird is disabled here, so nobody checked this patch is reachable)',
  'rules.saved': 'saved.',
  'rules.addOrReplace': 'Add / replace rule',
  'rules.warning':
    "The patch must already be promoted to stable (step 2 above) for that exact release_version — the dropdown only lists ones that qualify; typing both fields by hand is still possible, but the server rejects a combination that isn't actually live on stable.",

  'message.titleLabel': 'Screen title (optional)',
  'message.titlePlaceholder': 'Leave blank to use the app\'s built-in default title.',
  'message.titleHelper':
    "Shown as the heading on the force-update screen, for this exact release_version + patch number. Leave blank to keep whatever title this exact patch already has (never cleared by leaving this blank — and never inherited from a different patch). This is the English baseline: saving it drafts a translation for every other language, which a device only receives once approved — see Translations.",
  'message.label': 'Message (optional, English baseline)',
  'message.placeholder': 'Shown to the user in the force/optional update dialog.',
  'message.helper':
    "Saved alongside the rule above, to every platform checked, scoped to this exact release_version + patch number — leave blank to keep whatever message this exact patch already has rather than clearing it (a different patch's message is never reused). Saving a new language kicks off a background translation into every other enabled language — click Translations to review or edit the drafts.",
  'message.alsoSaved': 'Message updated.',
  'message.saveFailed': 'Failed to save message',

  'promote.title': 'Promote a Patch to Stable',
  'promote.description':
    'Moves one Shorebird patch from whatever channel it is on (e.g. "staging") onto "stable" — the channel Shorebird\'s updater checks by default. This is the approval step: nothing an engineer publishes reaches production users until it happens here. Pick one platform, or both when the same release_version + patch number applies to each.',
  'promote.platformLabel': 'Platform',
  'promote.noReleasesYet': '(no releases yet)',
  'promote.singlePlatformNote':
    'One platform at a time: a patch number belongs to a single platform, so the same number means a different change on the other one. Promote the other platform separately.',
  'promote.releaseVersionLabel': 'Release version',
  'promote.checkPlatformFirst': 'Check a platform to pick from its releases.',
  'promote.releasesLoadError': 'Could not load releases from Shorebird — type the version below manually.',
  'promote.releaseVersionPlaceholder': 'e.g. 1.0.1+2',
  'promote.patchToPromoteLabel': 'Patch to promote',
  'promote.pickReleaseFirst': 'Pick a release above to see its patches.',
  'promote.patchesLoadError': 'Could not load patches from Shorebird — type the number below manually.',
  'promote.pickerLoadingReleases': 'Loading releases…',
  'promote.pickerPromptRelease': 'Pick a release',
  'promote.pickerLoadingPatches': 'Loading patches…',
  'promote.pickerPromptPatch': 'Pick a patch on staging',
  'promote.requiredHelper':
    'Required either way — this is the patch that becomes reachable at all once promoted, not just the mandatory floor. "Also set as mandatory" below is an optional add-on that happens to reuse this same number.',
  'promote.alsoSetMandatory': 'Also set as mandatory',
  'promote.mandatoryHelper':
    'Immediately marks patch {patch} as the mandatory floor on the platform picked above, in this same submit, and only if the promotion itself succeeds. Leave unchecked for a plain optional release — the patch above still gets promoted and becomes available, just with no floor forcing anyone onto it.',
  'promote.messagePlaceholder': 'Shown to the user in the update dialog — force or optional, whichever applies.',
  'promote.messageHelper':
    'Saved for the platform picked above regardless of whether "Also set as mandatory" is checked, scoped to this exact release_version + patch number — the same message field the cards below edit.',
  'promote.submit': 'Promote to Stable',
  'promote.submitting': 'Promoting…',
  'promote.clearDraft': 'Clear draft',
  'promote.clearDraftTooltip': "Clears what's typed here only — does not delete this patch's saved title/message on the server.",

  'promote.impactHeading': 'What happens',
  'promote.impactNone': 'Pick a platform to preview what happens.',
  'promote.impactPlatforms': 'Platforms',
  'promote.impactVersion': 'Version',
  'promote.impactMandatory': 'Also mandatory',
  'promote.impactYes': 'Yes',
  'promote.impactNo': 'No',

  'rollback.title': 'Roll Back Stable to an Earlier Patch',
  'rollback.description':
    'Moves the "stable" channel — the one Shorebird\'s updater checks by default — back to a patch that was already promoted before. Use this only when a patch currently live in production turns out to have a problem. If step 3\'s mandatory floor for this platform references a newer patch than what you roll back to, it\'s automatically lowered to match — stable will never serve that higher number again until a fresh forward promote.',
  'rollback.platformLabel': 'Platform',
  'rollback.multiSelectHint': 'Multiple allowed',
  'rollback.currentPrefix': 'Currently on stable:',
  'rollback.releaseVersionLabel': 'Release version',
  'rollback.pickPlatformFirst': 'Pick a platform to see its releases.',
  'rollback.patchNumberLabel': 'Patch number',
  'rollback.pickReleaseFirst': 'Pick a release above to see its patches.',
  'rollback.pickerPromptPatch': 'Pick a patch',
  'rollback.noStablePatches':
    'Nothing on stable for this release yet — every patch is still on staging or another channel. Type the number below manually if you still mean to target one.',
  'rollback.confirm': "I understand this immediately moves {platform}'s production stable channel back to patch {patch}.",
  'rollback.confirmRollforward': 'I understand this immediately reactivates patch {patch} for {platform}, so it can be served again.',
  'rollback.submit': 'Roll Back to This Patch',
  'rollback.submitting': 'Rolling back…',
  'rollback.rollforwardSubmit': 'Roll Forward (Reactivate) This Patch',
  'rollback.rollforwardSubmitting': 'Rolling forward…',

  'nativeVersion.heading': 'Native version gate',
  'nativeVersion.help':
    'Separate from the mandatory floors above — this blocks the platform(s) checked below on the native app binary itself, for a gap no Shorebird patch can fix. Off by default; set a minimum version to turn it on. The app sends the blocked user to the store on its own (Play in-app update on Android, the App Store on iOS) — no link to configure here.',
  'nativeVersion.loadError': 'Could not load the native version gate. Reload to retry.',
  'nativeVersion.offShort': 'Off',
  'nativeVersion.onSummary': 'Min {version}',
  'nativeVersion.optionalOnSummary': 'Optional ≥ {version}',
  'nativeVersion.pickFromReleases': 'Pick from a known release',
  'nativeVersion.pickerPrompt': 'Pick a release to fill in its version + build',
  'nativeVersion.pickerEmpty': 'No releases yet',
  'nativeVersion.minVersionLabel': 'Minimum version',
  'nativeVersion.minVersionPlaceholder': 'e.g. 1.2.0',
  'nativeVersion.minBuildLabel': 'Minimum build (optional)',
  'nativeVersion.minBuildPlaceholder': 'e.g. 8',
  'nativeVersion.minBuildHelper': 'Only compared when the version above ties — leave blank to ignore build number.',
  'nativeVersion.messageLabel': 'Message (optional)',
  'nativeVersion.messagePlaceholder': 'Shown on the forced-update screen — leave blank for the app default.',
  'nativeVersion.titleLabel': 'Title (optional)',
  'nativeVersion.titlePlaceholder': 'Shown as the forced-update screen heading — leave blank for the app default.',
  'nativeVersion.reasonsLabel': 'Bullets shown on this screen',
  'nativeVersion.reasonSecurity': 'Better Security',
  'nativeVersion.reasonPerformance': 'Improved Performance',
  'nativeVersion.reasonFeature': 'New Features',
  'nativeVersion.translations': 'Translations…',
  'nativeVersion.alsoMandatory': 'Also set as mandatory',
  'nativeVersion.alsoMandatoryHelp':
    'Off: this saves only as a dismissible recommendation. On: the same version/title/message/reasons also become the hard floor above — same shape as Promote’s "Also set mandatory".',
}

const ZH: Dict = {
  'page.title': 'App 补丁策略',
  'page.loadErrorTitle': '无法加载补丁策略',
  'page.intro':
    '把 Shorebird 补丁发给真实用户:先在真机上测试,再提升(promote)——如果这个补丁要立刻变成强制更新楼层,顺手勾上"同时设为强制更新"。下面的卡片用来查看当前楼层,或者事后手动调整。',

  'common.tryAgain': '重试',
  'common.save': '保存',
  'common.saving': '保存中…',
  'common.clear': '清除',
  'common.translations': '翻译',

  'actions.heading': '我要',
  'actions.promote': '发布补丁(验证 + 提升)',
  'actions.flag': '改强制 / 可选标识',
  'actions.roll': '紧急回滚',

  'summary.mandatoryOn': '已设强制楼层',
  'summary.mandatoryOff': '无强制楼层',

  'record.heading': '补丁记录',
  'record.empty': '还没有相关记录。',
  'record.loadError': '无法加载最近记录。',
  'record.viewAll': '查看完整审计日志 →',

  'staging.title': '把设备切到 staging 模式',
  'staging.descBefore': '在测试设备本身操作——不是在这里——打开 app,进入',
  'staging.descAfter':
    ',在 3 秒内连续点版本号 7 次。会弹出提示确认 staging 模式已开启。开启之后,这台设备会静默下载并评估当前 staging 轨道上的任何补丁,跟真实用户设备对 stable 的行为一样——每次新补丁都不需要额外操作。再连点 7 次可以关闭。',

  'platform.notSyncedYet': '还没同步——等 Shorebird 可访问后刷新此页',

  'rules.heading': '强制更新楼层',
  'rules.help':
    '每一条规则都会强制下面勾选的平台上、恰好处于该 release_version 的客户端,升级到不低于所填补丁号。一个没有规则的 release_version 就完全没有楼层——包括全新的 release,新建时默认没有任何规则;补丁编号每个 release 都从 1 重新计数,所以旧 release 的规则永远不会套用到新 release 上。',
  'rules.loadError': '无法加载强制更新规则,刷新页面重试。',
  'rules.noneAny': '两个平台目前都没有设置强制更新规则。',
  'rules.pickFromStable': '从 stable 中选择',
  'rules.pickerLoading': '加载中…',
  'rules.pickerEmpty': 'stable 上还没有内容',
  'rules.pickerPrompt': '选择一个已上线 stable 的补丁',
  'rules.releaseVersionLabel': 'Release 版本号',
  'rules.releaseVersionPlaceholder': '例如 1.0.1+6',
  'rules.patchNumberLabel': '补丁编号',
  'rules.platformLabel': '平台',
  'rules.multiSelectHint': '可多选',
  'rules.checkPlatformFirst': '先勾选一个平台,才能从它的 stable 补丁里选。',
  'rules.unavailableHint': '只有这个 release_version + 补丁号也在该平台自己的 stable 上生效时,才能勾选它。',
  'rules.unchanged': '本来就是这个值——没有需要保存的。',
  'rules.unverified': '（未校验——本环境未配置 Shorebird，没人确认过这个补丁真的能下到）',
  'rules.saved': '已保存。',
  'rules.addOrReplace': '添加 / 替换规则',
  'rules.warning':
    '这个补丁必须已经在该 release_version 下提升到 stable(见上面第 2 步)——下拉框只会列出符合条件的组合;也可以手动填两个字段,但服务器会拒绝一个实际并不在 stable 上生效的组合。',

  'message.titleLabel': '屏幕标题(可选)',
  'message.titlePlaceholder': '留空则使用 app 内置的默认标题。',
  'message.titleHelper':
    '显示在强制更新界面的标题位置,只对应这一个具体的 release_version + patch number。留空就保持这个具体 patch 原来的标题(不会被清空,也不会沿用别的 patch 的标题)。这里填的是英文原文,保存后会自动生成其他语言的翻译草稿,审核通过后设备才会收到——见「Translations」。',
  'message.label': '提示信息(可选,英文原文)',
  'message.placeholder': '会显示在强制/可选更新弹窗里给用户看。',
  'message.helper':
    '会跟上面的规则一起保存,存到勾选的每个平台,只对应这一个具体的 release_version + patch number——留空就保持这个具体 patch 原来的文案,不会被清空,也不会沿用别的 patch 的文案。保存一种新语言的内容会自动触发对其他每个已启用语言的后台翻译——点"翻译"可以查看或编辑草稿。',
  'message.alsoSaved': '提示信息已更新。',
  'message.saveFailed': '保存提示信息失败',

  'promote.title': '把补丁提升到 Stable',
  'promote.description':
    '把一个 Shorebird 补丁从它当前所在的任何 channel(比如 "staging")挪到 "stable"——也就是 Shorebird 更新器默认检查的那个 channel。这是审批步骤:工程师发布的东西在这一步之前都到不了生产环境用户手上。可以只选一个平台,或者同一个 release_version + 补丁号同时适用两个平台时都选上。',
  'promote.platformLabel': '平台',
  'promote.noReleasesYet': '(还没有 release)',
  'promote.singlePlatformNote': '一次只能提升一个平台：补丁号只属于单个平台，同一个号在另一个平台上是另一次改动。另一个平台请单独提升。',
  'promote.releaseVersionLabel': 'Release 版本号',
  'promote.checkPlatformFirst': '先勾选一个平台,才能选它的 release。',
  'promote.releasesLoadError': '无法从 Shorebird 加载 release 列表——请在下方手动输入版本号。',
  'promote.releaseVersionPlaceholder': '例如 1.0.1+2',
  'promote.patchToPromoteLabel': '要提升的补丁',
  'promote.pickReleaseFirst': '先在上面选一个 release,才能看到它的补丁。',
  'promote.patchesLoadError': '无法从 Shorebird 加载补丁列表——请在下方手动输入编号。',
  'promote.pickerLoadingReleases': '正在加载 release…',
  'promote.pickerPromptRelease': '选择一个 release',
  'promote.pickerLoadingPatches': '正在加载补丁…',
  'promote.pickerPromptPatch': '选择一个在 staging 上的补丁',
  'promote.requiredHelper':
    '无论如何都必须填——这是提升之后会变得可访问的那个补丁,不只是强制楼层。下面的"同时设为强制更新"只是顺带复用这同一个补丁号的可选项。',
  'promote.alsoSetMandatory': '同时设为强制更新',
  'promote.mandatoryHelper':
    '在这次提交中,立刻把补丁 {patch} 设为上面所选平台的强制更新楼层,且仅在提升本身成功时生效。不勾选就是一次普通的可选发布——上面的补丁照样会被提升、变得可用,只是不会强制任何人更新。',
  'promote.messagePlaceholder': '会显示在更新弹窗里(强制或可选,视情况而定)。',
  'promote.messageHelper':
    '不管有没有勾选"同时设为强制更新",只要填了都会保存到上面所选的平台,只对应这一个具体的 release_version + patch number——跟下面卡片编辑的是同一个字段。',
  'promote.submit': '提升到 Stable',
  'promote.submitting': '提升中…',
  'promote.clearDraft': '清空草稿',
  'promote.clearDraftTooltip': '只清空这里输入的内容,不会删除服务器上这个 patch 已保存的 title/message。',

  'promote.impactHeading': '将会发生什么',
  'promote.impactNone': '先选平台才能预览。',
  'promote.impactPlatforms': '平台',
  'promote.impactVersion': '版本',
  'promote.impactMandatory': '同时强制',
  'promote.impactYes': '是',
  'promote.impactNo': '否',

  'rollback.title': '把 Stable 回滚到更早的补丁',
  'rollback.description':
    '把 "stable" channel(Shorebird 更新器默认检查的那个)挪回一个之前已经提升过的补丁。仅在生产环境当前上线的补丁出了问题时使用。如果第 3 步里这个平台的强制楼层指向一个比回滚目标更新的补丁,会自动降下来对齐——在重新正向 promote 之前,stable 不会再提供那个更高的编号。',
  'rollback.platformLabel': '平台',
  'rollback.multiSelectHint': '可多选',
  'rollback.currentPrefix': '现在 stable 上是:',
  'rollback.releaseVersionLabel': 'Release 版本号',
  'rollback.pickPlatformFirst': '先选一个平台,才能看到它的 release。',
  'rollback.patchNumberLabel': '补丁编号',
  'rollback.pickReleaseFirst': '先在上面选一个 release,才能看到它的补丁。',
  'rollback.pickerPromptPatch': '选择一个补丁',
  'rollback.noStablePatches': '这个 release 在 stable 上还没有内容——所有补丁还在 staging 或其他 channel 上。如果仍要指定,请在下方手动输入编号。',
  'rollback.confirm': '我知道这会立即把 {platform} 生产环境的 stable 通道回滚到补丁 {patch}。',
  'rollback.confirmRollforward': '我知道这会立即重新激活 {platform} 的补丁 {patch},让它重新可以被服务。',
  'rollback.submit': '回滚到这个补丁',
  'rollback.submitting': '回滚中…',
  'rollback.rollforwardSubmit': '重新激活这个补丁(前滚)',
  'rollback.rollforwardSubmitting': '重新激活中…',

  'nativeVersion.heading': '原生版本门槛',
  'nativeVersion.help':
    '跟上面的强制更新楼层是两回事——这个挡的是下面勾选的平台的原生安装包本身,针对 Shorebird 补丁根本修不了的差距。默认关闭,填了最低版本号才会生效。跳转商店是 app 自己处理的(Android 走 Play 应用内更新,iOS 跳 App Store)——这里不需要配链接。',
  'nativeVersion.loadError': '无法加载原生版本门槛,刷新页面重试。',
  'nativeVersion.offShort': '关闭',
  'nativeVersion.onSummary': '最低 {version}',
  'nativeVersion.optionalOnSummary': '非强制 ≥ {version}',
  'nativeVersion.pickFromReleases': '从已知 release 中选择',
  'nativeVersion.pickerPrompt': '选择一个 release,自动填入版本号 + build 号',
  'nativeVersion.pickerEmpty': '还没有 release',
  'nativeVersion.minVersionLabel': '最低版本号',
  'nativeVersion.minVersionPlaceholder': '例如 1.2.0',
  'nativeVersion.minBuildLabel': '最低 build 号(可选)',
  'nativeVersion.minBuildPlaceholder': '例如 8',
  'nativeVersion.minBuildHelper': '只有版本号相同时才会比较——留空表示不比较 build 号。',
  'nativeVersion.messageLabel': '提示信息(可选)',
  'nativeVersion.messagePlaceholder': '会显示在强制更新界面上——留空则用 app 里的默认文案。',
  'nativeVersion.titleLabel': '标题(可选)',
  'nativeVersion.titlePlaceholder': '会显示为强制更新界面的标题——留空则用 app 里的默认文案。',
  'nativeVersion.reasonsLabel': '这个界面要显示的卖点',
  'nativeVersion.reasonSecurity': '更好的安全性',
  'nativeVersion.reasonPerformance': '性能提升',
  'nativeVersion.reasonFeature': '新功能',
  'nativeVersion.translations': '翻译…',
  'nativeVersion.alsoMandatory': '同时设为强制更新',
  'nativeVersion.alsoMandatoryHelp':
    '不勾:只保存为可以关闭的建议更新。勾选:同一个版本号/标题/提示信息/卖点也会变成上面的强制门槛——跟 Promote 那边的「同时设为强制更新」是同一个思路。',
}

const DICTS: Record<Locale, Dict> = { en: EN, zh: ZH }

function format(template: string, vars?: Record<string, string | number>) {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  )
}

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'zh' ? 'zh' : 'en'
  } catch {
    return 'en'
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale)

  function setLocale(next: Locale) {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Best-effort — a private window or blocked storage just means the choice doesn't
      // survive a reload, not a reason to fail the switch itself.
    }
  }

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => format(DICTS[locale][key] ?? DICTS.en[key] ?? key, vars),
    }),
    [locale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider')
  return ctx
}

export function LocaleSwitcher() {
  const { locale, setLocale } = useLocale()
  return (
    <Select value={locale} onValueChange={(value) => setLocale(value as Locale)}>
      <SelectTrigger className="w-28">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en">English</SelectItem>
        <SelectItem value="zh">中文</SelectItem>
      </SelectContent>
    </Select>
  )
}
